/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { type ProductModel } from '@/api/types/products-types';
import { useLocale } from '@/hooks/useLocale';
import Head from 'next/head';
import {
  generateAggregateRating,
  generateReviewsList,
} from '@/features/reviews/utils/generateReviewsSchema';
import { getPathnameFromAsPath } from '@/utils/urlHelpers';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { getBaseSchemaUrl } from '@/utils/common';
import { type ProductReviewsResponse } from '@/features/reviews';
import { useFeatureFlag } from '@/features/globalConfig';
import { AuthenticDiscoveriesMetaData } from './AuthenticDiscoveriesMetaData';

type CustomReviewsMetaDataProps = {
  product: ProductModel | undefined;
  productReviews: ProductReviewsResponse | undefined;
};

export const CustomReviewsMetaData = ({ product, productReviews }: CustomReviewsMetaDataProps) => {
  const locale = useLocale();
  const productName = product?.itemDescription;
  const router = useLegacyRouter();
  const baseUrl = getBaseSchemaUrl(locale);
  const pathname = getPathnameFromAsPath(router.asPath);

  const authenticDiscoveryEnabled = useFeatureFlag('AUTHENTIC_DISCOVERY_ENABLED') === 'true';

  if (!product) {
    return null;
  }

  if (authenticDiscoveryEnabled) {
    return <AuthenticDiscoveriesMetaData product={product} />;
  }

  if (!productReviews) {
    return null;
  }

  const aggregateRatingSchema = JSON.stringify({
    '@context': 'https://schema.org/',
    '@id': `${baseUrl}${pathname}`,
    aggregateRating: {
      ...generateAggregateRating(productReviews),
      itemReviewed: {
        '@type': 'IndividualProduct',
        name: productName,
      },
    },
  }).replace(/</g, '\\u003c');

  const legacyReviewsSchema = JSON.stringify({
    review: generateReviewsList(productReviews),
    '@context': 'https://schema.org/',
    '@id': `${baseUrl}${pathname}`,
  }).replace(/</g, '\\u003c');

  return (
    <Head>
      <script
        key={'aggregateRatingSchema'}
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: aggregateRatingSchema }}
      />
      <script
        key={'customerReviewsSchema'}
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: legacyReviewsSchema }}
      />
    </Head>
  );
};
